import {
  BlockStack,
  Button,
  Checkbox,
  Text,
  TextField,
} from "@shopify/polaris"
import { PlusCircleIcon } from "@shopify/polaris-icons"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import DraggableList from "@/component/DraggableList"
import EditorPopup from "@/component/EditorPopup"
import ModuleCard from "@/component/ModuleCard"
import type { ThemeBottomSectionImageItem, ThemeImageBannerRef } from "@/models/ITrackingPageOptions"
import { THEME_LAYOUT_LEFT_ROOT_ID } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/themeLayoutLeftScroll"

import ImageUpload from "./ImageUpload"
import { newThemeBottomId } from "./themeBottomSectionDefaults"
import {
  clampThemeLayoutAltOrDestinationUrl,
  THEME_LAYOUT_ALT_AND_DESTINATION_URL_MAX_LENGTH,
} from "./themeLayoutFieldLimits"
import {
  reconcileTopColumnOrder,
  SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID,
} from "./themeLayoutShowcaseDraft"
import { useThemeLayoutSubmoduleDirty } from "./ThemeLayoutSubmoduleDirtyContext"
import { useSyncEditingImageOnCommonImageDeleted } from "./useSyncEditingImageOnCommonImageDeleted"

export interface ShowcaseTopColumnsEditorProps {
  images: ThemeBottomSectionImageItem[]
  columnOrder: string[]
  onCommitImages: (next: ThemeBottomSectionImageItem[]) => Promise<void>
  onCommitColumnOrder: (next: string[]) => Promise<void>
  /** 弹窗编辑期间通知外部刷新预览 overlay，关闭时传 null */
  onPopupDraftChange?: (draft: ThemeBottomSectionImageItem[] | null) => void
}

const ShowcaseTopColumnsEditor = (props: ShowcaseTopColumnsEditorProps) => {
  const { images, columnOrder, onCommitImages, onCommitColumnOrder, onPopupDraftChange } = props
  const { t } = useTranslation("tracking_page")
  const { setSubmoduleDirty } = useThemeLayoutSubmoduleDirty()

  const [editingImageId, setEditingImageId] = useState<string | null>(null)
  const [imageSnapshot, setImageSnapshot] = useState<ThemeBottomSectionImageItem | undefined>(undefined)
  const [imageDraftBanner, setImageDraftBanner] = useState<ThemeImageBannerRef | undefined>(undefined)
  const [imageDraftUrl, setImageDraftUrl] = useState("")
  const [imageDraftAlt, setImageDraftAlt] = useState("")
  const [imageDraftNewWin, setImageDraftNewWin] = useState(true)

  // Edit image 弹窗打开期间，正在编辑的图片若在图库被删除，则同步清空弹窗内的图片
  useSyncEditingImageOnCommonImageDeleted({
    active: editingImageId !== null,
    setImageDraftBanner,
    setImageSnapshot,
  })

  const handleReorderTopColumns = useCallback(
    (list: Array<{ id: string }>) => {
      void onCommitColumnOrder(list.map((row) => row.id))
    },
    [onCommitColumnOrder],
  )

  const handleAddImage = useCallback(async () => {
    if (images.length >= 2) {
      return
    }
    const item: ThemeBottomSectionImageItem = {
      id: newThemeBottomId(),
      destinationUrl: "",
      openInNewWindow: true,
      enabled: true,
    }

    await onCommitImages([...images, item])
  }, [images, onCommitImages])

  const handleImageRowEnabled = useCallback(
    async (itemId: string, checked: boolean) => {
      const next = images.map((i) => (i.id === itemId ? { ...i, enabled: checked } : i))

      await onCommitImages(next)
    },
    [images, onCommitImages],
  )

  // 弹窗编辑期间同步预览 overlay：用当前本地草稿替换正在编辑的图片，其余图片保持原样
  useEffect(() => {
    if (!editingImageId || !onPopupDraftChange) {
      return
    }
    const draftImages = images.map((item) => {
      if (item.id !== editingImageId) {
        return item
      }

      return {
        ...item,
        destinationUrl: imageDraftUrl,
        image: imageDraftBanner
          ? { ...imageDraftBanner, alt: imageDraftAlt.trim() }
          : imageDraftAlt.trim() ? { shopifyFileId: "", url: "", alt: imageDraftAlt.trim() } : undefined,
        openInNewWindow: imageDraftNewWin,
      }
    })

    onPopupDraftChange(draftImages)
  }, [editingImageId, imageDraftBanner, imageDraftUrl, imageDraftAlt, imageDraftNewWin, images, onPopupDraftChange])

  const handleImageDraftBannerChange = useCallback(async (next: ThemeImageBannerRef | undefined) => {
    setImageDraftBanner(next ? { ...next, alt: next.alt || "" } : next)
  }, [])

  const handleImageDraftAltChange = useCallback((next: string) => {
    setImageDraftAlt(clampThemeLayoutAltOrDestinationUrl(next))
  }, [])

  const handleImageDraftUrlChange = useCallback((next: string) => {
    setImageDraftUrl(clampThemeLayoutAltOrDestinationUrl(next))
  }, [])

  const handleImageDraftNewWindowChange = useCallback((next: boolean) => {
    setImageDraftNewWin(next)
  }, [])

  const handleEditImageClick = useCallback((id: string) => {
    const item = images.find((imageItem) => imageItem.id === id)

    if (!item) {
      return
    }
    // stub image（url 和 shopifyFileId 均为空）视为"无图片"，避免 ImageUpload 误认为有图
    const isRealBanner = Boolean(item.image?.shopifyFileId?.trim() || item.image?.url?.trim())

    setImageSnapshot({ ...item, image: item.image ? { ...item.image } : undefined })
    setImageDraftUrl(clampThemeLayoutAltOrDestinationUrl(item.destinationUrl))
    setImageDraftAlt(clampThemeLayoutAltOrDestinationUrl(item.image?.alt || ""))
    setImageDraftBanner(isRealBanner ? item.image : undefined)
    setImageDraftNewWin(item.openInNewWindow)
    setEditingImageId(id)
  }, [images])

  const handleSaveImagePopup = useCallback(async () => {
    if (!editingImageId) {
      return
    }
    const next = images.map((i) => {
      if (i.id !== editingImageId) {
        return i
      }

      const trimmedAlt = imageDraftAlt.trim()

      return {
        ...i,
        destinationUrl: imageDraftUrl.trim(),
        image: imageDraftBanner
          ? { ...imageDraftBanner, alt: trimmedAlt }
          : trimmedAlt ? { shopifyFileId: "", url: "", alt: trimmedAlt } : undefined,
        openInNewWindow: imageDraftNewWin,
      }
    })

    await onCommitImages(next)
    onPopupDraftChange?.(null)
    setImageSnapshot(undefined)
    setEditingImageId(null)
  }, [
    editingImageId,
    imageDraftAlt,
    imageDraftBanner,
    imageDraftNewWin,
    imageDraftUrl,
    images,
    onCommitImages,
    onPopupDraftChange,
  ])

  const isImagePopupDirty = useMemo(() => {
    if (!imageSnapshot) {
      return false
    }

    const currentImage = imageSnapshot.image
    const draftImage = imageDraftBanner

    const hasDifferentUrl = imageSnapshot.destinationUrl.trim() !== imageDraftUrl.trim()
    const hasDifferentAlt = (currentImage?.alt || "").trim() !== imageDraftAlt.trim()
    const hasDifferentOpenInNewWindow = imageSnapshot.openInNewWindow !== imageDraftNewWin
    const hasDifferentBanner =
      (currentImage?.shopifyFileId || "") !== (draftImage?.shopifyFileId || "")
      || (currentImage?.url || "") !== (draftImage?.url || "")

    return hasDifferentUrl || hasDifferentAlt || hasDifferentOpenInNewWindow || hasDifferentBanner
  }, [
    imageSnapshot,
    imageDraftAlt,
    imageDraftBanner,
    imageDraftNewWin,
    imageDraftUrl,
  ])

  useEffect(() => {
    setSubmoduleDirty("showcase-top-columns-editor-popup", isImagePopupDirty)

    return () => {
      setSubmoduleDirty("showcase-top-columns-editor-popup", false)
    }
  }, [isImagePopupDirty, setSubmoduleDirty])

  const handleRemoveEditingImageRow = useCallback(async () => {
    if (!editingImageId) {
      return
    }
    await onCommitImages(images.filter((i) => i.id !== editingImageId))
    onPopupDraftChange?.(null)
    setImageSnapshot(undefined)
    setEditingImageId(null)
  }, [editingImageId, images, onCommitImages, onPopupDraftChange])

  // Cancel / X：store 从未被弹窗修改过，只需清空本地草稿和 overlay
  const handleCancelImagePopup = useCallback(() => {
    if (!editingImageId) {
      return
    }
    onPopupDraftChange?.(null)
    setImageSnapshot(undefined)
  }, [editingImageId, onPopupDraftChange])

  const orderLookupRow = useMemo(
    () => ({
      id: SHOWCASE_TOP_ORDER_LOOKUP_ROW_ID,
      checked: true,
      disabled: false,
      dragHandleDisabled: false,
      children: (
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 pr-1">
          <div className="min-w-0 flex-1 [&_.Polaris-Labelled__LabelWrapper]:max-w-none">
            <Checkbox
              label={<Text as={"span"} tone={"base"}>{t("themeLayoutShowcaseOrderLookupWidget")}</Text>}
              checked
              disabled
            />
          </div>
        </div>
      ),
    }),
    [t],
  )

  const draggableImageCards = useMemo(
    () =>
      images.map((item) => ({
        id: item.id,
        checked: item.enabled !== false,
        children: (
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 pr-1">
            <div className="min-w-0 flex-1 [&_.Polaris-Labelled__LabelWrapper]:max-w-none">
              <Checkbox
                label={t("ThemeBottomImageSection")}
                checked={item.enabled !== false}
                onChange={(checked) => void handleImageRowEnabled(item.id, checked)}
              />
            </div>
            <Button variant="plain" onClick={() => handleEditImageClick(item.id)}>
              {t("ThemeBottomEdit")}
            </Button>
          </div>
        ),
      })),
    [handleEditImageClick, handleImageRowEnabled, images, t],
  )

  const topColumnCards = useMemo(() => {
    const cardById = new Map(
      [orderLookupRow, ...draggableImageCards].map((card) => [card.id, card]),
    )

    return reconcileTopColumnOrder(columnOrder, images)
      .map((id) => cardById.get(id))
      .filter((card): card is typeof orderLookupRow => card !== undefined)
  }, [columnOrder, draggableImageCards, images, orderLookupRow])

  const canAddImage = images.length < 2

  const leftRootPortalEl =
    typeof document !== "undefined" ? document.getElementById(THEME_LAYOUT_LEFT_ROOT_ID) : null

  const editorPopup = (
    <EditorPopup
      open={editingImageId !== null}
      title={t("ThemeBottomEditImageTitle")}
      close={() => setEditingImageId(null)}
      onDismiss={() => {
        handleCancelImagePopup()
        setEditingImageId(null)
      }}
      active={isImagePopupDirty}
      primaryAction={{
        content: t("Done"),
        onAction: () => void handleSaveImagePopup(),
      }}
      secondaryActions={{
        content: t("ThemeBottomModalCancel"),
        onAction: handleCancelImagePopup,
      }}
    >
      <ModuleCard>
        <div className={"flex flex-col gap-3"}>
          <div>
            <Text as="p" variant="bodyMd">
              {t("ThemeBottomImageSection")}
            </Text>
            <div className="mt-2">
              <ImageUpload
                banner={imageDraftBanner}
                onPersistBanner={handleImageDraftBannerChange}
              />
            </div>
          </div>
          <div className={"flex flex-col gap-1"}>
            {/* <TextField */}
            {/*   label={t("AltTextOptional")} */}
            {/*   value={imageDraftAlt} */}
            {/*   onChange={handleImageDraftAltChange} */}
            {/*   maxLength={THEME_LAYOUT_ALT_AND_DESTINATION_URL_MAX_LENGTH} */}
            {/*   autoComplete="off" */}
            {/* /> */}
            <TextField
              label={t("ThemeBottomDestinationUrl")}
              value={imageDraftUrl}
              onChange={handleImageDraftUrlChange}
              maxLength={THEME_LAYOUT_ALT_AND_DESTINATION_URL_MAX_LENGTH}
              autoComplete="off"
              helpText={t("LeaveLink")}
            />
            <Checkbox
              label={t("ThemeBottomOpenInNewWindow")}
              checked={imageDraftNewWin}
              onChange={handleImageDraftNewWindowChange}
            />
          </div>
        </div>
      </ModuleCard>

      <div className={"p-4"}>
        <Button
          tone="critical"
          variant="secondary"
          onClick={() => void handleRemoveEditingImageRow()}
        >
          {t("RemoveThisSection")}
        </Button>
      </div>
    </EditorPopup>
  )

  return (
    <BlockStack gap="200">
      <Text variant="bodyMd" as="p">
        {t("themeLayoutShowcaseColumns")}
      </Text>

      <DraggableList
        cards={topColumnCards}
        setCards={(list) => handleReorderTopColumns(list as Array<{ id: string }>)}
        className="flex flex-col gap-1"
      />

      <div className={"linkButton"}>
        <Button
          fullWidth
          size="large"
          disabled={!canAddImage}
          variant="secondary"
          icon={PlusCircleIcon}
          onClick={() => void handleAddImage()}
        >
          {t("ThemeBottomAddImage")}
        </Button>
      </div>

      {leftRootPortalEl ? createPortal(editorPopup, leftRootPortalEl) : editorPopup}
    </BlockStack>
  )
}

export default ShowcaseTopColumnsEditor
